import { useCallback, useMemo, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Avatar,
  Box,
  Button,
  Collapse,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconChevronDown,
  IconInfoCircle,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import moment from "moment-timezone";
// @ts-ignore - JS context module without types
import { useSalon } from "../../../../../context/SalonContext";
import { useApp } from "../../../../../context/AppContext";
import { useServiceAdjustmentForms } from "../../../../../context/ServiceAdjustmentFormsContext";
import { ServiceAdjustmentForm } from "../../../../../types/serviceAdjustmentForms";
import type { SalonService } from "../../../../../types/salon";
import AdditionalQuestionsSection from "../TransactionDetailModal/TransactionBookedServiceModal/AdditionalQuestionsSection";
// @ts-ignore - JS context module without types — used only to resolve the
// selected client's display name for the client card / review chip.
import { useClient } from "../../../../../context/ClientContext";
// @ts-ignore - JS component without types
import ClientLookup from "../../../../common/ClientLookup";
// @ts-ignore - JS util without types
import { filterBookableActiveStaff } from "../../../../../utils/staffUtils";
import StandardizedDatePicker from "../../../../common/StandardizedDatePicker";
import { getCreatedTransactions } from "../../../../../utils/bookingResponse";
import {
  formatDurationShort,
  getSalonTimezone,
  parseApiDateTime,
} from "../../../../../utils/timeUtils";
import { formatPhoneNumber } from "../../../../../utils/phoneUtils";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../../../types/transactions";
import { notifySuccess } from "../../../../../utils/notify";
import type { CalendarEventType } from "../../../../../types/calendar";
import { draftOverlap } from "../../booking/draftOverlap";
import { BookingMode, ServiceRow, emptyRow } from "../../booking/bookingTypes";
import { canDeleteServiceRow } from "../../booking/guards";
import {
  buildEditLineItemsPayload,
  rowStartIso,
} from "../../booking/rescheduleCommit";
import {
  TIME_STEP,
  activeAdjustmentForms,
  adjustmentPriceDelta,
  appendRow,
  applySuggestedSlot,
  isSuggestionSelected,
  buildBookingPayload,
  finalStaffServiceId as sharedFinalStaffServiceId,
  fmtTime,
  fromMinutes,
  isDraftBookable,
  patchRowIn,
  performableServiceIds as sharedPerformableServiceIds,
  REVIEW_TAX_NOTE,
  removeRowFrom,
  selectServicePatch,
  selectStaffPatch,
  serviceIdsForRow,
  serviceQuote,
  serviceLength,
  staffIdsForService,
  suggestionQuery,
  toMinutes,
  toggleAdjustmentOption,
} from "../../booking/draftModel";
import {
  SuggestedSlot,
  useSuggestedTimes,
} from "../../booking/useSuggestedTimes";
import { useNextBlocker } from "../../booking/useNextBlocker";
import styles from "./MultiServiceAppointmentForm.module.css";

export { emptyRow };
export type { BookingMode, ServiceRow };

/** Rows update: always an updater, applied by the owner to its LATEST rows. */
export type RowsUpdater = (rows: ServiceRow[]) => ServiceRow[];

interface MultiServiceAppointmentFormProps {
  /**
   * The booking day ("YYYY-MM-DD"). Rows arrive with this date set; a date
   * edit is expressed as a rows update that sets the new date on the rows.
   */
  initialDate: string;
  onSuccess?: (created: TransactionResponse[]) => void;
  onCancel?: () => void;
  /**
   * "create" POSTs a new appointment; "rebook" POSTs a new appointment
   * carrying `rebooking_source_uuid`; "edit" PUTs the source appointment's
   * existing booked services in place (+ adds new rows). Create/rebook report
   * via `onSuccess`; edit via `onEditSuccess`.
   */
  mode?: BookingMode;
  /** Source transaction for edit (target) / rebook (rebooking_source_uuid). */
  sourceTransaction?: GetTransactionsResponse | null;
  /**
   * Called after a successful edit, with any transaction ids the backend
   * split cross-day services into (the caller refetches them + the source).
   */
  onEditSuccess?: (splitTransactionIds: string[]) => void;
  /**
   * Controlled draft: the booking session owns rows / client / active row so
   * the calendar can mirror them as temp events. `onRowsChange` receives an
   * UPDATER, never a precomputed array, so two updates in one tick compose.
   */
  rows: ServiceRow[];
  onRowsChange: (update: RowsUpdater) => void;
  clientUuid: string;
  onClientChange: (clientUuid: string) => void;
  activeUid: number;
  onActiveChange: (uid: number) => void;
  /** Loaded calendar events, for the bottom overlap warning. */
  calendarEvents?: CalendarEventType[];
}

const fmtDateLong = (d: string | null): string =>
  d ? moment(d, "YYYY-MM-DD").format("MMM D, YYYY") : "";

const staffLabel = (e: any): string =>
  `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() || e.id;

/**
 * Multi-service booking wizard. Step 1 selects and schedules service rows (one
 * provider / time / durations each, all on the booking day); step 2 reviews
 * and confirms. All rows submit as one flat booked_services list.
 */
export default function MultiServiceAppointmentForm({
  initialDate,
  onSuccess,
  onCancel,
  mode = "create",
  sourceTransaction = null,
  onEditSuccess,
  rows,
  onRowsChange: setRows,
  clientUuid,
  onClientChange: setClientUuid,
  activeUid: selectedUid,
  onActiveChange: setSelectedUid,
  calendarEvents = [],
}: MultiServiceAppointmentFormProps) {
  const { employees, services, businessInfo } = useSalon() as any;
  const {
    createAppointment,
    editTransactionLineItems,
    addLineItemToTransaction,
  } = useApp();
  const { allClients } = (useClient() as any) || { allClients: [] };
  const { adjustmentForms, formJunctions } = useServiceAdjustmentForms();

  const timezone: string = getSalonTimezone(businessInfo);

  const [step, setStep] = useState<1 | 2>(1);

  // Next unused row id, derived so nothing has to be kept in sync with `rows`.
  const nextUid = rows.reduce((max, r) => Math.max(max, r.uid), 0) + 1;

  const [durOpen, setDurOpen] = useState<Record<number, boolean>>({});
  // Per-row expand state for the "Additional questions" (service-adjustment)
  // section — defaults open so any required adjustment is visible up front.
  const [adjOpen, setAdjOpen] = useState<Record<number, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const serviceById = useMemo(() => {
    const map = new Map<string, SalonService>();
    (services || []).forEach((s: SalonService) => map.set(s.id, s));
    return map;
  }, [services]);

  const serviceOptions = useMemo(
    () => (services || []).map((s: any) => ({ value: s.id, label: s.name })),
    [services],
  );

  const timeOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    for (let m = 0; m < 1440; m += TIME_STEP) {
      const v = fromMinutes(m);
      opts.push({ value: v, label: fmtTime(v) });
    }
    return opts;
  }, []);

  // A sequenced start time (previous start + service duration) may not land on
  // the 15-min grid; a Mantine Select renders a value that isn't in its data as
  // blank, so include the current value when it's off-grid.
  const timeDataFor = (current: string) =>
    current && !timeOptions.some((o) => o.value === current)
      ? [{ value: current, label: fmtTime(current) }, ...timeOptions]
      : timeOptions;

  const bookableStaff = useMemo(
    () => filterBookableActiveStaff(employees || []),
    [employees],
  );

  const allStaffOptions = useMemo(
    () =>
      bookableStaff.map((e: any) => ({ value: e.id, label: staffLabel(e) })),
    [bookableStaff],
  );

  const staffOptionsForService = (salonServiceId: string) => {
    const staffIds = staffIdsForService(services, salonServiceId);
    return bookableStaff
      .filter((e: any) => staffIds.has(e.id))
      .map((e: any) => ({ value: e.id, label: staffLabel(e) }));
  };

  // Services at least one bookable staff member can perform. Used to keep a
  // provider-less row from offering a service nobody can do.
  // `null` while services haven't loaded → don't restrict yet.
  const performableServiceIds = useMemo<Set<string> | null>(
    () =>
      sharedPerformableServiceIds(
        services,
        bookableStaff.map((e: any) => e.id),
      ),
    [services, bookableStaff],
  );

  // Service options for a row: when the row already has a staff member (e.g.
  // pre-populated from a day-view staff column), restrict to what that staff
  // can do; when no staff yet, restrict to services SOME bookable staff can
  // perform (never offer a service nobody can do).
  const serviceOptionsForRow = (row: ServiceRow) => {
    const allowed = new Set(
      serviceIdsForRow(
        row,
        serviceOptions.map((o: { value: string }) => o.value),
        services,
        performableServiceIds,
      ),
    );
    return serviceOptions.filter((o: { value: string }) =>
      allowed.has(o.value),
    );
  };

  // Staff options for a row: with a service chosen, its providers first and
  // everyone else greyed out (disabled) at the bottom; with no service yet,
  // every bookable staff member (staff-first flow).
  const staffOptionsForRow = (row: ServiceRow) => {
    if (!row.salonServiceId) return allStaffOptions;
    const qualified = staffOptionsForService(row.salonServiceId);
    const ok = new Set(qualified.map((o: { value: string }) => o.value));
    const others = allStaffOptions
      .filter((o: { value: string }) => !ok.has(o.value))
      .map((o: { value: string; label: string }) => ({ ...o, disabled: true }));
    return [...qualified, ...others];
  };

  // Active service-adjustment forms for a given service (mirrors
  // AppointmentForm.js): junction-matched, is_active, sorted by priority with
  // questions/options likewise sorted. Empty when the service has none.
  const activeFormsFor = useCallback(
    (salonServiceId: string): ServiceAdjustmentForm[] =>
      activeAdjustmentForms(adjustmentForms, formJunctions, salonServiceId),
    [adjustmentForms, formJunctions],
  );

  // Single-select per question: replace whichever option was selected for this
  // question with the newly chosen one (or clear it when optionId is "").
  //
  // Neither delta is applied to the row's own values: the backend adds the
  // option's DURATION and PRICE delta as a sub line item from the option id,
  // so the row keeps (and sends) the unadjusted base duration and base price
  // (applying them too would double-count). The duration delta lives on the
  // row as adjustmentDuration (length / end time / temp events); price
  // adjustments surface in the displayed totals via priceDeltaFor().
  const toggleAdjustment = (
    uid: number,
    forms: ServiceAdjustmentForm[],
    optionId: string,
    questionId: string,
  ) =>
    setRows((prev) =>
      prev.map((r) =>
        r.uid === uid
          ? toggleAdjustmentOption(r, forms, optionId, questionId)
          : r,
      ),
    );

  // Total price delta from a row's selected adjustment options (the backend
  // applies this via sub line items; here it's added to the DISPLAYED price so
  // the review/totals reflect what the client will be charged).
  const priceDeltaFor = (row: ServiceRow): number =>
    adjustmentPriceDelta(row, activeFormsFor(row.salonServiceId));
  const displayedPrice = (row: ServiceRow): number =>
    (row.price || 0) + priceDeltaFor(row);

  const serviceName = (r: ServiceRow): string =>
    serviceById.get(r.salonServiceId)?.name || "";
  const staffName = (id: string): string => {
    const e = bookableStaff.find((x: any) => x.id === id);
    return e ? `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() : "";
  };

  // Patch one row from ITS latest value (computed inside the updater, so a
  // second update in the same tick sees the first). Single-day rule: a date
  // change applies to every row (per-service TIMES still differ); the owner
  // turns a date change into a change of the booking day.
  const updateRow = (
    uid: number,
    patchFor: (row: ServiceRow) => Partial<ServiceRow>,
  ) => setRows((prev) => patchRowIn(prev, uid, patchFor));
  const patchRow = (uid: number, patch: Partial<ServiceRow>) =>
    updateRow(uid, () => patch);

  const onSelectService = (uid: number, salonServiceId: string | null) => {
    const svc = serviceById.get(salonServiceId || "");
    // Keep a provider who can perform it (staff-first); adopt the kept
    // provider's price/durations. Clearing (null) empties only the service.
    updateRow(uid, (row) => selectServicePatch(row, salonServiceId, svc));
  };

  const onSelectStaff = (uid: number, employeeIdRaw: string | null) => {
    const employeeId = employeeIdRaw || "";
    // A provider who can't perform the row's service clears it; otherwise
    // re-apply this provider's price/durations. Clearing ("") keeps the
    // service.
    updateRow(uid, (row) =>
      selectStaffPatch(row, employeeId, serviceById.get(row.salonServiceId)),
    );
  };

  const addRow = () => {
    // New rows inherit the booking day (single-day rule) and, as an initial
    // default only, start after the previous row (its start + full stride).
    const uid = nextUid;
    setRows((prev) => appendRow(prev, uid, initialDate));
    setSelectedUid(uid);
  };

  // The first service is the appointment's primary service and is never
  // removable (guard); the owner re-points the active row if it was removed.
  const removeRow = (uid: number) =>
    setRows((prev) => removeRowFrom(prev, uid));

  // One overlap warning for the whole draft (warning only), against its
  // other rows and the loaded calendar (the edited appointment never counts).
  const overlap = draftOverlap(rows, calendarEvents, {
    excludeTransactionId:
      mode === "edit" ? sourceTransaction?.transaction.id : undefined,
    serviceName: (r) => serviceById.get(r.salonServiceId)?.name || "",
  });

  // Every row needs a service, a qualified provider, a date and a time (past
  // dates allowed).
  const rowsValid = isDraftBookable(rows, clientUuid, services);
  // Next stays clickable: clicking it on an incomplete draft shows why.
  const nextBlocker = useNextBlocker(rows, clientUuid, services);
  const shownBlocker = nextBlocker.shown;
  const blocks = nextBlocker.flags;

  const anyServiceChosen = rows.some((r) => r.salonServiceId);
  const totalMinutes = rows.reduce((sum, r) => sum + serviceLength(r), 0);
  const chosenMinutes = rows
    .filter((r) => r.salonServiceId)
    .reduce((sum, r) => sum + serviceLength(r), 0);
  // Displayed price includes each row's adjustment price delta (the client-facing
  // amount); the base row.price is still what's sent (backend adds the delta).
  const totalPrice = rows.reduce((sum, r) => sum + displayedPrice(r), 0);

  // Suggested times: every chosen service in row order with its provider
  // (aligned) and the first row's time as the requested start. Shared rule:
  // draftModel.suggestionQuery.
  const sugg = suggestionQuery(rows);
  const suggDate = sugg.date;
  const hasSuggestionQuery = sugg.serviceIds.length > 0;

  // Server-suggested start times (GET /suggested-times), in the order
  // returned. Empty/error → the section is hidden.
  const {
    suggestions,
    loading: sugLoading,
    error: sugError,
  } = useSuggestedTimes({
    serviceIds: sugg.serviceIds,
    staffIds: sugg.staffIds,
    startTime: sugg.startTime,
    date: suggDate,
    timezone,
  });

  // Group suggested slots by salon-local day so the UI can show one bold day
  // header with a horizontally-scrolling row of time pills beneath it (keeps the
  // vertical footprint small even with many openings, and handles the next-day
  // fallback the endpoint can return).
  const suggestionsByDay = useMemo(() => {
    const groups: { day: string; label: string; slots: SuggestedSlot[] }[] = [];
    const indexByDay = new Map<string, number>();
    suggestions.forEach((slot) => {
      const m = parseApiDateTime(slot.start_dt, timezone);
      const day = m.format("YYYY-MM-DD");
      let idx = indexByDay.get(day);
      if (idx === undefined) {
        idx = groups.length;
        indexByDay.set(day, idx);
        groups.push({ day, label: m.format("dddd, MMM D"), slots: [] });
      }
      groups[idx].slots.push(slot);
    });
    return groups;
  }, [suggestions, timezone]);

  // Apply a slot: each service row takes its own start and (when it has
  // none) provider from the slot; every row adopts the slot's day.
  const applySuggestion = (slot: SuggestedSlot) => {
    if (!hasSuggestionQuery) return;
    setRows((prev) => applySuggestedSlot(prev, sugg, slot, timezone, services));
  };

  // The staff_service_id to book with: the row's chosen provider + service.
  const finalStaffServiceId = (r: ServiceRow): string | undefined =>
    sharedFinalStaffServiceId(services, r);

  const startIso = (r: ServiceRow): string => rowStartIso(r, timezone);

  // Advance to the review step.
  const handleGoToReview = () => {
    const blocker = nextBlocker.reveal();
    if (blocker) {
      if (blocker.uid !== null) setSelectedUid(blocker.uid);
      return;
    }
    setError(null);
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!rowsValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // EDIT: update the source appointment's existing booked services in place
      // (time / staff / durations / price), AND add any newly-added rows as new
      // booked services on the same transaction (via the add-line-item
      // endpoint). Removing an existing service is still not supported here.
      if (mode === "edit") {
        if (!sourceTransaction) {
          throw new Error("Missing appointment to edit.");
        }
        const existingRows = rows.filter(
          (r) => r.sourceBookedServiceId && r.sourceLineItemId,
        );
        const newRows = rows.filter(
          (r) => !(r.sourceBookedServiceId && r.sourceLineItemId),
        );

        let splitTransactionIds: string[] = [];
        if (existingRows.length > 0) {
          const payload = buildEditLineItemsPayload(
            sourceTransaction.transaction.id,
            existingRows,
            timezone,
            finalStaffServiceId,
          );
          const edited = await editTransactionLineItems(payload);
          splitTransactionIds = edited?.split_transaction_uuids ?? [];
        }

        if (newRows.length > 0) {
          // Same shape the "Add Service" flow posts (add-line-item endpoint).
          // base_duration_mins is the unadjusted base; the backend adds the
          // adjustment options' minutes from their ids.
          await addLineItemToTransaction(sourceTransaction.transaction.id, {
            booked_services: newRows.map((r) => ({
              staff_service_id: finalStaffServiceId(r),
              unit_price: r.price,
              start_dt: startIso(r),
              base_duration_mins: r.baseDuration,
              processing_time_mins: r.processingTime,
              finishing_time_mins: r.finishingTime,
              buffer_time_mins: r.bufferTime,
              ...(r.adjustmentOptionIds && r.adjustmentOptionIds.length > 0
                ? { service_adjustment_form_option_ids: r.adjustmentOptionIds }
                : {}),
            })),
          });
        }

        notifySuccess(
          `${formatDurationShort(totalMinutes)} · ${formatCurrency(totalPrice)}`,
          { title: "Appointment updated" },
        );
        onEditSuccess?.(splitTransactionIds);
        return;
      }

      // CREATE / REBOOK: POST a new appointment. Rebook carries the source
      // transaction uuid (same field the RebookModal uses) + per-service prices.
      const payload = buildBookingPayload({
        rows,
        clientUuid,
        mode,
        sourceTransactionId: sourceTransaction?.transaction.id,
        timezone,
        services,
      });

      const response = await createAppointment(payload);
      notifySuccess(
        `${formatDurationShort(totalMinutes)} · ${formatCurrency(totalPrice)}`,
        {
          title:
            mode === "rebook" ? "Appointment prebooked" : "Appointment booked",
        },
      );
      onSuccess?.(getCreatedTransactions(response));
    } catch (e: any) {
      setError(
        e?.message ||
          (mode === "edit"
            ? "Could not update the appointment."
            : "Could not create the appointment."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Review-step derived values -----------------------------------------
  const client = (allClients || []).find((c: any) => c.id === clientUuid);
  const clientName = client
    ? `${client.first_name ?? ""} ${client.last_name ?? ""}`.trim()
    : "";
  const clientInitials =
    clientName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p: string) => p[0]?.toUpperCase())
      .join("") || "—";
  const clientPhone: string = formatPhoneNumber(
    `${client?.area_code ?? ""}${client?.phone_number ?? ""}`,
  );

  const reviewDates = Array.from(
    new Set(rows.filter((r) => r.date).map((r) => r.date as string)),
  ).sort();
  const reviewDateText =
    reviewDates.length === 0
      ? "no dates set"
      : reviewDates.length === 1
        ? fmtDateLong(reviewDates[0])
        : `${fmtDateLong(reviewDates[0])} – ${fmtDateLong(reviewDates[reviewDates.length - 1])}`;
  const reviewSub = `${rows.length} ${rows.length === 1 ? "service" : "services"} · ${reviewDateText} · ${formatDurationShort(totalMinutes)}`;

  const subtotal = totalPrice;

  // ---- Step 1: select / schedule ------------------------------------------
  const renderStepOne = () => (
    <Box className={styles.body}>
      <Text className={styles.subtitle}>
        Click the calendar to move the selected service
      </Text>

      <Text className={styles.microLabel}>Client</Text>
      <Box className={styles.clientRow}>
        {clientUuid ? (
          <Box className={styles.clientCard}>
            <Avatar radius="xl" size={38} className={styles.clientCardAvatar}>
              {clientInitials}
            </Avatar>
            <Box className={styles.clientCardBody}>
              <Text span className={styles.clientCardName}>
                {clientName}
              </Text>
              {clientPhone && (
                <Text span className={styles.clientCardPhone}>
                  {clientPhone}
                </Text>
              )}
            </Box>
            <UnstyledButton
              className={styles.clientCardChange}
              onClick={() => setClientUuid("")}
              aria-label="Change client"
            >
              Change
            </UnstyledButton>
          </Box>
        ) : (
          /* Original inline client search/create dropdown (with its result
             list), styled as our unselected search box — no wrapper. */
          <ClientLookup
            value={clientUuid}
            onChange={(v: string) => setClientUuid(v)}
            error={blocks(null, "client")}
            label={null}
            required={false}
            placeholder="Search or add a client..."
            pinCreate
            leftSection={<IconSearch size={16} />}
            radius="md"
            size="md"
          />
        )}
      </Box>

      {/* Client is required first: everything below is hidden until a client is
          selected. Next stays clickable and explains what's missing. */}
      {clientUuid && (
        <>
          <Text className={styles.microLabel}>Services</Text>

          <Stack gap={12}>
            {rows.map((row, rowIndex) => {
              const chosen = !!row.salonServiceId;
              const selected = row.uid === selectedUid;
              const len = serviceLength(row);
              // Show the end time whenever a start time exists — even before a
              // service is picked (a blank row defaults to a 30-min length), so the
              // "to <end>" reads and can be edited without a service selected.
              const endText = row.time
                ? fmtTime(fromMinutes(toMinutes(row.time) + len))
                : "";
              const adjForms = chosen ? activeFormsFor(row.salonServiceId) : [];
              // The first service can't be removed (booking / rebook / reschedule
              // all require at least the primary service to exist).
              const canRemove = canDeleteServiceRow(rows, row.uid);
              return (
                <Box
                  key={row.uid}
                  className={`${styles.serviceCard} ${selected ? styles.serviceCardSelected : ""}`}
                  onClick={() => setSelectedUid(row.uid)}
                >
                  {canRemove && (
                    <Box className={styles.cardHeader}>
                      <Box style={{ flex: 1 }} />
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeRow(row.uid);
                        }}
                        aria-label={`Remove service ${rowIndex + 1}`}
                      >
                        <IconTrash size={15} />
                      </ActionIcon>
                    </Box>
                  )}

                  <Stack gap={11}>
                    <Select
                      size="md"
                      placeholder="Add service…"
                      error={blocks(row.uid, "service")}
                      data={serviceOptionsForRow(row)}
                      value={row.salonServiceId || null}
                      onChange={(v) => onSelectService(row.uid, v)}
                      searchable
                      clearable
                      clearButtonProps={{ "aria-label": "Clear service" }}
                      comboboxProps={{ withinPortal: false }}
                      // Each service's length and price with this row's
                      // provider (their overrides, else the salon defaults).
                      renderOption={({ option }) => {
                        const quote = serviceQuote(
                          serviceById.get(option.value),
                          row.employeeId,
                        );
                        return (
                          <Group
                            gap={8}
                            wrap="nowrap"
                            justify="space-between"
                            w="100%"
                          >
                            <Text size="sm">{option.label}</Text>
                            <Text size="xs" c="dimmed">
                              {`${formatDurationShort(quote.minutes)} · ${formatCurrency(quote.price)}`}
                            </Text>
                          </Group>
                        );
                      }}
                    />
                    <Select
                      size="md"
                      placeholder="Select provider"
                      error={blocks(row.uid, "provider")}
                      data={staffOptionsForRow(row)}
                      value={row.employeeId || null}
                      onChange={(v) => onSelectStaff(row.uid, v)}
                      searchable
                      clearable
                      clearButtonProps={{ "aria-label": "Clear provider" }}
                      comboboxProps={{ withinPortal: false }}
                    />

                    {/* Date / time sit inside the card, after service and
                        provider (design field order). */}
                    <StandardizedDatePicker
                      value={row.date}
                      onChange={(v) => patchRow(row.uid, { date: v })}
                      error={blocks(row.uid, "date")}
                      // Any date is bookable (create, rebook, reschedule): no
                      // past floor, no future cap.
                      allowPast
                      maxWeeksAhead={null}
                      variant="trigger"
                      placeholder="Select date"
                      showWeekCounter={false}
                    />

                    {adjForms.length > 0 && (
                      <Box onClick={(e: any) => e.stopPropagation()}>
                        <AdditionalQuestionsSection
                          serviceId={row.salonServiceId}
                          activeAdjustmentForms={adjForms}
                          selectedOptions={row.adjustmentOptionIds || []}
                          onToggleOption={(optionId, questionId) =>
                            toggleAdjustment(
                              row.uid,
                              adjForms,
                              optionId,
                              questionId,
                            )
                          }
                          isExpanded={adjOpen[row.uid] ?? true}
                          onToggleExpanded={() =>
                            setAdjOpen((p) => ({
                              ...p,
                              [row.uid]: !(p[row.uid] ?? true),
                            }))
                          }
                          labelColor="#555555"
                          labelSize="12.5px"
                          labelWeight={600}
                          iconSize={15}
                        />
                      </Box>
                    )}

                    <Group gap={10} wrap="nowrap" align="center">
                      <Select
                        size="md"
                        error={blocks(row.uid, "time")}
                        data={timeDataFor(row.time)}
                        value={row.time}
                        onChange={(v) =>
                          patchRow(row.uid, { time: v || row.time })
                        }
                        searchable
                        comboboxProps={{ withinPortal: false }}
                        style={{ flex: 1, minWidth: 0 }}
                        aria-label="Start time"
                      />
                      <Text className={styles.toWord}>to</Text>
                      <Box className={styles.endField}>{endText}</Box>
                      {row.time && (
                        <Text className={styles.durMeta}>
                          {formatDurationShort(len)}
                        </Text>
                      )}
                    </Group>

                    {chosen && (
                      <NumberInput
                        size="md"
                        value={row.price}
                        onChange={(v) =>
                          patchRow(row.uid, { price: Number(v) || 0 })
                        }
                        prefix="$ "
                        min={0}
                        decimalScale={2}
                        hideControls
                        clampBehavior="none"
                        aria-label="Price"
                      />
                    )}

                    {chosen && (
                      <Box>
                        <Box
                          component="button"
                          type="button"
                          className={styles.durationToggle}
                          onClick={(e: any) => {
                            e.stopPropagation();
                            setDurOpen((p) => ({
                              ...p,
                              [row.uid]: !p[row.uid],
                            }));
                          }}
                        >
                          Adjust service durations
                          <IconChevronDown
                            size={15}
                            style={{
                              transform: durOpen[row.uid]
                                ? "rotate(180deg)"
                                : "none",
                              transition: "transform .15s ease",
                            }}
                          />
                        </Box>
                        <Collapse in={!!durOpen[row.uid]}>
                          <Group grow mt={10}>
                            <NumberInput
                              label="Base"
                              value={row.baseDuration}
                              onChange={(v) =>
                                patchRow(row.uid, {
                                  baseDuration: Number(v) || 0,
                                })
                              }
                              min={0}
                              clampBehavior="none"
                            />
                            <NumberInput
                              label="Processing"
                              value={row.processingTime}
                              onChange={(v) =>
                                patchRow(row.uid, {
                                  processingTime: Number(v) || 0,
                                })
                              }
                              min={0}
                              clampBehavior="none"
                            />
                          </Group>
                          <Group grow mt={10}>
                            <NumberInput
                              label="Finishing"
                              value={row.finishingTime}
                              onChange={(v) =>
                                patchRow(row.uid, {
                                  finishingTime: Number(v) || 0,
                                })
                              }
                              min={0}
                              clampBehavior="none"
                            />
                            <NumberInput
                              label="Buffer"
                              value={row.bufferTime}
                              onChange={(v) =>
                                patchRow(row.uid, {
                                  bufferTime: Number(v) || 0,
                                })
                              }
                              min={0}
                              clampBehavior="none"
                            />
                          </Group>
                        </Collapse>
                      </Box>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          <Button
            variant="default"
            leftSection={<IconPlus size={16} />}
            onClick={addRow}
            radius={8}
            className={styles.addBtn}
          >
            Add another service
          </Button>

          {hasSuggestionQuery && !!suggDate && !sugError && (
            <Box className={styles.suggestions}>
              <Group gap={8} align="center" mb={9}>
                <Text className={styles.microLabel}>Suggested times</Text>
                <Text className={styles.sugTotal}>
                  {formatDurationShort(
                    suggestions[0]?.duration_mins ?? chosenMinutes,
                  )}{" "}
                  total
                </Text>
              </Group>
              {sugLoading ? (
                <Text className={styles.sugMuted}>Finding open times…</Text>
              ) : suggestions.length === 0 ? (
                <Text className={styles.sugMuted}>No open times</Text>
              ) : (
                <Stack gap={12}>
                  {suggestionsByDay.map((grp) => (
                    <Box key={grp.day} className={styles.sugDayGroup}>
                      <Text className={styles.sugDayLabel}>{grp.label}</Text>
                      <Box className={styles.sugScrollRow}>
                        {grp.slots.map((slot, i) => {
                          const start = parseApiDateTime(
                            slot.start_dt,
                            timezone,
                          );
                          const end = parseApiDateTime(slot.end_dt, timezone);
                          return (
                            <Box
                              key={`${slot.start_dt}-${i}`}
                              component="button"
                              type="button"
                              className={styles.pill}
                              data-on={isSuggestionSelected(
                                rows,
                                slot,
                                timezone,
                              )}
                              aria-pressed={isSuggestionSelected(
                                rows,
                                slot,
                                timezone,
                              )}
                              onClick={() => applySuggestion(slot)}
                            >
                              {start.format("h:mm A")} – {end.format("h:mm A")}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </>
      )}

      {error && (
        <Text c="red" size="sm">
          {error}
        </Text>
      )}
    </Box>
  );

  // ---- Step 2: review / confirm -------------------------------------------
  const renderStepTwo = () => (
    <Box className={styles.body}>
      <Box className={styles.clientChip}>
        <Avatar radius="xl" size={36} className={styles.chipAvatar}>
          {clientInitials}
        </Avatar>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text className={styles.chipName}>{clientName || "No client"}</Text>
          <Text className={styles.chipSub}>{reviewSub}</Text>
        </Box>
      </Box>

      <Group gap={10} align="center" mt={20}>
        <Text className={styles.microLabel}>Services</Text>
        <Anchor
          component="button"
          type="button"
          onClick={() => setStep(1)}
          className={styles.editLink}
        >
          <IconPencil size={14} />
          Edit services
        </Anchor>
      </Group>

      <Stack gap={10} mt={9}>
        {rows.map((row) => {
          const len = serviceLength(row);
          const endText = row.time
            ? fmtTime(fromMinutes(toMinutes(row.time) + len))
            : "";
          return (
            <Box key={row.uid} className={styles.reviewCard}>
              <Group gap={10} align="baseline" wrap="nowrap">
                <Text className={styles.reviewName}>{serviceName(row)}</Text>
                <Text className={styles.reviewPrice}>
                  {formatCurrency(displayedPrice(row))}
                </Text>
              </Group>
              <Stack gap={9} mt={12}>
                <Group gap={12} align="baseline" wrap="nowrap">
                  <Text className={styles.reviewMeta}>When</Text>
                  <Text className={styles.reviewValue}>
                    {row.date
                      ? `${fmtDateLong(row.date)}${row.time ? ` · ${fmtTime(row.time)} – ${endText}` : ""}`
                      : row.time
                        ? `${fmtTime(row.time)} – ${endText}`
                        : "Not scheduled"}
                  </Text>
                </Group>
                <Group gap={12} align="baseline" wrap="nowrap">
                  <Text className={styles.reviewMeta}>Provider</Text>
                  <Text className={styles.reviewValue}>
                    {staffName(row.employeeId)}
                  </Text>
                </Group>
                <Group gap={12} align="baseline" wrap="nowrap">
                  <Text className={styles.reviewMeta}>Duration</Text>
                  <Text className={styles.reviewValue}>
                    {formatDurationShort(len)}
                  </Text>
                </Group>
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <ReviewTotals subtotal={subtotal} />

      {error && (
        <Text c="red" size="sm" mt="sm">
          {error}
        </Text>
      )}
    </Box>
  );

  // ---- Footer --------------------------------------------------------------
  // Step-1 (select/schedule) shows the "N services · N min · $" summary bottom-left.
  // Step-2 (review/checkout) shows NO summary there (totals live in the card).
  const footerSummary =
    step === 2
      ? ""
      : anyServiceChosen
        ? `${rows.filter((r) => r.salonServiceId).length} ${rows.filter((r) => r.salonServiceId).length === 1 ? "service" : "services"} · ${formatDurationShort(chosenMinutes)} · ${formatCurrency(totalPrice)}`
        : "Pick a service to continue";

  return (
    <Box className={styles.panel}>
      {step === 1 ? renderStepOne() : renderStepTwo()}

      {overlap && step === 1 && (
        <Group gap={6} wrap="nowrap" className={styles.overlap} role="status">
          <IconInfoCircle size={14} />
          <Text className={styles.conflictText}>{overlap}</Text>
        </Group>
      )}
      <Box className={styles.footer}>
        {step === 1 && shownBlocker ? (
          <Group
            gap={6}
            wrap="nowrap"
            className={`${styles.footerSummary} ${styles.footerBlocker}`}
            role="alert"
          >
            <IconAlertTriangle size={15} style={{ flexShrink: 0 }} />
            <Text className={styles.footerBlockerText}>
              {shownBlocker.message}
            </Text>
          </Group>
        ) : (
          <Text className={styles.footerSummary}>{footerSummary}</Text>
        )}
        {step === 2 ? (
          // Review page: "Back" returns to the service/schedule step (parity
          // with the "Edit services" link).
          <Button
            variant="subtle"
            color="gray"
            onClick={() => setStep(1)}
            disabled={submitting}
          >
            Back
          </Button>
        ) : (
          onCancel && (
            <Button
              variant="subtle"
              color="gray"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
          )
        )}
        {step === 1 ? (
          <Button
            color="var(--brand-teal)"
            onClick={handleGoToReview}
            radius={8}
          >
            Next
          </Button>
        ) : (
          <Button
            color="var(--brand-teal)"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!rowsValid}
            radius={8}
          >
            {mode === "edit"
              ? "Save changes"
              : mode === "rebook"
                ? "Prebook appointment"
                : "Book appointment"}
          </Button>
        )}
      </Box>
    </Box>
  );
}

/**
 * Review totals: Subtotal plus the existing tax note (same as mobile). No
 * Total or points; the rest is shown in the checkout modal.
 */
function ReviewTotals({ subtotal }: { subtotal: number }) {
  return (
    <Stack gap={9} mt={18} data-testid="review-totals">
      <Group gap={14} align="baseline" wrap="nowrap">
        <Text className={styles.totalsLabel}>Subtotal</Text>
        <Text className={styles.totalsValue}>{formatCurrency(subtotal)}</Text>
      </Group>
      <Text className={styles.taxPending}>{REVIEW_TAX_NOTE}</Text>
    </Stack>
  );
}
